// apps/web/src/components/shell/topbar-slot.tsx — Fase 17.8, Plano 09 (D-16; UI-SPEC U-02 e U-10).
// Slot da topbar: a topbar registra o elemento-alvo e qualquer tela de gestão pode projetar nele (por portal) um
// controle que, em telas largas, fica ao lado do breadcrumb (hoje, o seletor de perfil do /painel).
import { createContext, useContext, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface ContextoSlot {
  readonly alvo: HTMLElement | null;
  readonly registrarAlvo: (elemento: HTMLElement | null) => void;
}

// Fora do provider (testes isolados de componentes) não há alvo: quem preenche cai no seu lugar normal.
const ContextoSlotTopbar = createContext<ContextoSlot>({ alvo: null, registrarAlvo: () => undefined });

/** Guarda o elemento-alvo da topbar; fica acima da topbar e do conteúdo no AppShell. */
export function ShellTopbarSlotProvider({ children }: { readonly children: ReactNode }): ReactElement {
  const [alvo, setAlvo] = useState<HTMLElement | null>(null);
  const valor = useMemo<ContextoSlot>(() => ({ alvo, registrarAlvo: setAlvo }), [alvo]);
  return <ContextoSlotTopbar.Provider value={valor}>{children}</ContextoSlotTopbar.Provider>;
}

/** Ponto de encaixe dentro da topbar (`ref` callback registra o elemento). */
export function AlvoSlotTopbar(): ReactElement {
  const { registrarAlvo } = useContext(ContextoSlotTopbar);
  return <div ref={registrarAlvo} data-testid="topbar-slot" className="topbar-slot" />;
}

/** Projeta o conteúdo no alvo da topbar; sem alvo registrado devolve null (o chamador decide o plano B). */
export function PreencherSlotTopbar({ children }: { readonly children: ReactNode }): ReactNode {
  const { alvo } = useContext(ContextoSlotTopbar);
  return alvo ? createPortal(children, alvo) : null;
}

/** `true` quando existe um alvo registrado, para o chamador escolher entre o slot e a primeira linha da página. */
export function useTemAlvoSlotTopbar(): boolean {
  return useContext(ContextoSlotTopbar).alvo !== null;
}
